<template>
    <!-- Toast Notification -->
    <Toast
        :show="toast.show"
        :type="toast.type"
        :message="toast.message"
        @close="toast.show = false"
    />

    <div class="h-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div v-if="localField" class="p-4 space-y-6">
            <div class="flex justify-between items-center">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Field Properties</h3>
                <button
                    @click="$emit('close')"
                    type="button"
                    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Field Type (Read-only after creation) -->
            <div>
                <div class="flex items-center justify-between mb-1">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Field Type
                    </label>
                    <button
                        @click="showFieldTypeInfo = true"
                        type="button"
                        class="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Learn about field types"
                    >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
                <div class="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {{ getFieldTypeLabel(localField.field_type) }}
                </div>
            </div>

            <!-- Field Label -->
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Question / Label <span class="text-red-500">*</span>
                </label>
                <textarea
                    v-model="localField.label"
                    rows="2"
                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter your question or field label"
                />
            </div>

            <!-- Help Text -->
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Help Text
                </label>
                <textarea
                    v-model="localField.help_text"
                    rows="2"
                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Optional instructions or guidance"
                />
            </div>

            <!-- Required Toggle -->
            <div>
                <div class="flex items-center">
                    <input
                        v-model="localField.is_required"
                        type="checkbox"
                        :disabled="hasDependency"
                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <label class="ml-2 text-sm" :class="hasDependency ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'">
                        Required Field
                    </label>
                </div>
                <p v-if="hasDependency" class="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Required status is controlled by the dependency rule
                </p>
            </div>

            <!-- Field-Specific Configuration -->
            <div v-if="needsOptions" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Options
                </label>
                <div class="space-y-2">
                    <div v-for="(option, index) in fieldConfig.options" :key="index" class="flex items-center space-x-2">
                        <input
                            v-model="fieldConfig.options[index]"
                            type="text"
                            class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            placeholder="Option text"
                        />
                        <button
                            @click="removeOption(index)"
                            type="button"
                            class="text-red-500 hover:text-red-700"
                        >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <button
                        @click="addOption"
                        type="button"
                        class="w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded text-sm text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
                    >
                        + Add Option
                    </button>
                </div>
            </div>

            <!-- Number/Decimal Configuration -->
            <div v-if="localField.field_type === 'number_input' || localField.field_type === 'decimal_input'" class="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                <div class="grid grid-cols-2 gap-3">
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min</label>
                        <input
                            v-model.number="fieldConfig.min"
                            type="number"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max</label>
                        <input
                            v-model.number="fieldConfig.max"
                            type="number"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Unit</label>
                    <input
                        v-model="fieldConfig.unit"
                        type="text"
                        placeholder="e.g., kg, °C, meters"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                </div>
            </div>

            <!-- Rating Configuration -->
            <div v-if="localField.field_type === 'rating_scale'" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Rating</label>
                <select
                    v-model.number="fieldConfig.max"
                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                    <option :value="3">3 Stars</option>
                    <option :value="5">5 Stars</option>
                    <option :value="10">10 Points</option>
                </select>
            </div>

            <!-- Field Supplements -->
            <div v-if="localField.field_type !== 'signature'" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Supplements</h4>
                <div class="space-y-3">
                    <!-- Notes -->
                    <div class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div class="flex items-center mb-2">
                            <input
                                v-model="localField.allow_notes"
                                type="checkbox"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <label class="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                📝 Allow Notes/Comments
                            </label>
                        </div>
                        <div v-if="localField.allow_notes" class="ml-6">
                            <label class="flex items-center">
                                <input
                                    v-model="localField.require_notes"
                                    type="checkbox"
                                    class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                />
                                <span class="ml-2 text-xs text-gray-600 dark:text-gray-400">Require notes</span>
                            </label>
                        </div>
                    </div>

                    <!-- Photos (hide for photo_capture and file_upload field types) -->
                    <div v-if="localField.field_type !== 'photo_capture' && localField.field_type !== 'file_upload'" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div class="flex items-center mb-2">
                            <input
                                v-model="localField.allow_photos"
                                type="checkbox"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <label class="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                📷 Allow Photo Capture
                            </label>
                        </div>
                        <div v-if="localField.allow_photos" class="ml-6">
                            <label class="flex items-center">
                                <input
                                    v-model="localField.require_photos"
                                    type="checkbox"
                                    class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                />
                                <span class="ml-2 text-xs text-gray-600 dark:text-gray-400">Require photos</span>
                            </label>
                        </div>
                    </div>

                    <!-- Attachments (hide for photo_capture and file_upload field types) -->
                    <div v-if="localField.field_type !== 'photo_capture' && localField.field_type !== 'file_upload'" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div class="flex items-center mb-2">
                            <input
                                v-model="localField.allow_attachments"
                                type="checkbox"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <label class="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                📎 Allow File Attachments
                            </label>
                        </div>
                        <div v-if="localField.allow_attachments" class="ml-6">
                            <label class="flex items-center">
                                <input
                                    v-model="localField.require_attachments"
                                    type="checkbox"
                                    class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                />
                                <span class="ml-2 text-xs text-gray-600 dark:text-gray-400">Require attachments</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Dependencies -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div class="flex items-center justify-between mb-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-white">Dependencies</h4>
                    <button
                        @click="showDependencyInfo = true"
                        type="button"
                        class="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Learn about dependencies"
                    >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Show this field only when certain conditions are met
                </p>

                <div v-if="dependencyConfig.parent_field_id" class="space-y-3 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <!-- Parent Field -->
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Show this field when
                        </label>
                        <select
                            v-model="dependencyConfig.parent_field_id"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option :value="null">No dependency</option>
                            <option v-for="field in availableParentFields" :key="field.id" :value="field.id">
                                {{ field.label || 'Untitled Field' }}
                            </option>
                        </select>
                    </div>

                    <!-- Condition -->
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Condition
                        </label>
                        <select
                            v-model="dependencyConfig.condition"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Not Equals</option>
                            <option value="greater_than">Greater Than</option>
                            <option value="less_than">Less Than</option>
                            <option value="contains">Contains</option>
                            <option value="in_array">In Array</option>
                        </select>
                    </div>

                    <!-- Value -->
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Value
                        </label>
                        <input
                            v-model="dependencyConfig.value"
                            type="text"
                            placeholder="Expected value"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>

                    <!-- Action -->
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Then
                        </label>
                        <select
                            v-model="dependencyConfig.action"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="show_optional">Show field (optional)</option>
                            <option value="show_and_require">Show field (required)</option>
                            <option value="hide">Hide field</option>
                        </select>
                    </div>

                    <!-- Remove Dependency -->
                    <button
                        @click="removeDependency"
                        type="button"
                        class="w-full py-2 border border-red-300 dark:border-red-600 rounded text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900"
                    >
                        Remove Dependency
                    </button>
                </div>

                <button
                    v-else
                    @click="addDependency"
                    type="button"
                    class="w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded text-sm text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
                >
                    + Add Dependency Rule
                </button>
            </div>

            <!-- Desired Answer Configuration -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div class="flex items-center justify-between mb-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-white">Desired Answer</h4>
                    <button
                        @click="showDesiredAnswerInfo = true"
                        type="button"
                        class="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Learn about desired answers"
                    >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    For validation, scoring, and reporting
                </p>

                <div class="space-y-3">
                    <!-- Desired Answer Type -->
                    <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Answer Type
                        </label>
                        <select
                            v-model="desiredAnswerConfig.type"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option v-for="option in availableAnswerTypes" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>

                    <!-- Exact Value / Comparison Value (only shown when needed) -->
                    <div v-if="shouldShowValueInput" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            <span v-if="desiredAnswerConfig.type === 'exact_value'">Expected Value</span>
                            <span v-else-if="desiredAnswerConfig.type === 'greater_than'">Greater than</span>
                            <span v-else-if="desiredAnswerConfig.type === 'greater_than_equal'">Greater than or equal to</span>
                            <span v-else-if="desiredAnswerConfig.type === 'less_than'">Less than</span>
                            <span v-else-if="desiredAnswerConfig.type === 'less_than_equal'">Less than or equal to</span>
                            <span v-else>Value</span>
                            <span v-if="localField.field_type === 'multiple_choice' || localField.field_type === 'dropdown_select'" class="text-xs text-gray-500 dark:text-gray-400">(must match one of the options above)</span>
                            <span v-else-if="localField.field_type === 'multiple_selection'" class="text-xs text-gray-500 dark:text-gray-400">(comma-separated values from options above)</span>
                            <span v-else-if="localField.field_type === 'decimal_input'" class="text-xs text-gray-500 dark:text-gray-400">(2 decimal places)</span>
                            <span v-else-if="localField.field_type === 'number_input' || localField.field_type === 'rating_scale'" class="text-xs text-gray-500 dark:text-gray-400">(integer)</span>
                        </label>
                        <input
                            v-if="localField.field_type === 'decimal_input'"
                            v-model.number="desiredAnswerConfig.value"
                            type="number"
                            step="0.01"
                            placeholder="Enter value"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <input
                            v-else-if="localField.field_type === 'number_input' || localField.field_type === 'rating_scale'"
                            v-model.number="desiredAnswerConfig.value"
                            type="number"
                            step="1"
                            placeholder="Enter value"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <input
                            v-else
                            v-model="desiredAnswerConfig.value"
                            type="text"
                            placeholder="Enter expected value"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>

                    <!-- Range -->
                    <div v-if="desiredAnswerConfig.type === 'range'" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg space-y-2">
                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Min
                                    <span v-if="localField.field_type === 'decimal_input'" class="text-xs text-gray-500 dark:text-gray-400">(2 decimals)</span>
                                    <span v-else-if="localField.field_type === 'number_input' || localField.field_type === 'rating_scale'" class="text-xs text-gray-500 dark:text-gray-400">(integer)</span>
                                </label>
                                <input
                                    v-model.number="desiredAnswerConfig.min"
                                    type="number"
                                    :step="localField.field_type === 'decimal_input' ? '0.01' : '1'"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                            <div>
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Max
                                    <span v-if="localField.field_type === 'decimal_input'" class="text-xs text-gray-500 dark:text-gray-400">(2 decimals)</span>
                                    <span v-else-if="localField.field_type === 'number_input' || localField.field_type === 'rating_scale'" class="text-xs text-gray-500 dark:text-gray-400">(integer)</span>
                                </label>
                                <input
                                    v-model.number="desiredAnswerConfig.max"
                                    type="number"
                                    :step="localField.field_type === 'decimal_input' ? '0.01' : '1'"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Any Of / None Of -->
                    <div v-if="desiredAnswerConfig.type === 'any_of' || desiredAnswerConfig.type === 'none_of'" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {{ desiredAnswerConfig.type === 'any_of' ? 'Acceptable Values' : 'Unacceptable Values' }}
                        </label>
                        <div class="space-y-2">
                            <div v-for="(value, index) in desiredAnswerConfig.values" :key="index" class="flex items-center space-x-2">
                                <input
                                    v-model="desiredAnswerConfig.values[index]"
                                    type="text"
                                    class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                    placeholder="Value"
                                />
                                <button
                                    @click="removeDesiredAnswerValue(index)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <button
                                @click="addDesiredAnswerValue"
                                type="button"
                                class="w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded text-xs text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
                            >
                                + Add Value
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Save Button -->
            <div class="sticky bottom-0 bg-white dark:bg-gray-800 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                    @click="saveField"
                    type="button"
                    class="w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    Save Field
                </button>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
            <div class="text-center">
                <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <p class="text-sm">Select a field to edit its properties</p>
            </div>
        </div>

        <!-- Info Modals -->
        <!-- Field Type Info Modal -->
        <div v-if="showFieldTypeInfo" class="fixed inset-0 z-[9999] overflow-y-auto" @click="showFieldTypeInfo = false">
            <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 z-[9998]"></div>
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full z-[9999]" @click.stop>
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="flex items-start justify-between mb-4">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Field Types Guide</h3>
                            <button @click="showFieldTypeInfo = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div class="space-y-4 text-sm text-gray-700 dark:text-gray-300 max-h-96 overflow-y-auto">
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">✓ Yes/No</h4>
                                <p>Simple binary choice. User selects either Yes or No.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Basic confirmations, compliance checks</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">✓✗ Pass/Fail/NA</h4>
                                <p>Three-option assessment with Pass, Fail, or Not Applicable.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Quality checks, inspections, audits</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">◉ Multiple Choice</h4>
                                <p>User selects ONE option from a list of choices (radio buttons).</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Single-answer questions with predefined options</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">☑ Checkboxes</h4>
                                <p>User can select MULTIPLE options from a list (checkboxes).</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Multiple-answer questions, feature selections</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">▼ Dropdown</h4>
                                <p>User selects one option from a dropdown menu. Good for long lists.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Long option lists, categories, locations</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">📝 Short Text</h4>
                                <p>Single-line text input for brief responses.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Names, serial numbers, short descriptions</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">📄 Long Text</h4>
                                <p>Multi-line text area for detailed responses.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Comments, descriptions, observations, notes</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">#️⃣ Number</h4>
                                <p>Integer number input. Can set min/max values and units.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Counts, quantities, whole number measurements</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">.# Decimal</h4>
                                <p>Decimal number input. Can set min/max values and units.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Precise measurements, temperatures, weights</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">⭐ Rating</h4>
                                <p>Star or point-based rating scale (3, 5, or 10 points).</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Quality ratings, satisfaction scores, assessments</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">📅 Date</h4>
                                <p>Date picker for selecting a specific date.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Expiry dates, installation dates, deadlines</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">📅⏰ Date & Time</h4>
                                <p>Picker for both date and time selection.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Scheduled events, timestamps, appointments</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">⏰ Time</h4>
                                <p>Time picker for selecting hours and minutes.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Start/end times, duration tracking</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">📷 Photo</h4>
                                <p>Photo capture or upload field.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Visual documentation, evidence, before/after photos</p>
                            </div>
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">📎 File</h4>
                                <p>File upload field for documents and attachments.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Reports, certificates, supporting documents</p>
                            </div>
                            <div>
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">✍️ Signature</h4>
                                <p>Digital signature capture field.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Use for: Approvals, acknowledgments, sign-offs</p>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button @click="showFieldTypeInfo = false" type="button" class="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Dependency Info Modal -->
        <div v-if="showDependencyInfo" class="fixed inset-0 z-[9999] overflow-y-auto" @click="showDependencyInfo = false">
            <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 z-[9998]"></div>
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full z-[9999]" @click.stop>
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="flex items-start justify-between mb-4">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Dependencies Guide</h3>
                            <button @click="showDependencyInfo = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div class="space-y-4 text-sm text-gray-700 dark:text-gray-300">
                            <div>
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">What are Dependencies?</h4>
                                <p>Dependencies allow you to show or hide fields based on answers to OTHER questions. This creates dynamic, conditional forms that adapt to user responses.</p>
                            </div>

                            <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h4 class="font-semibold text-blue-900 dark:text-blue-300 mb-2">How to Set Up a Dependency:</h4>
                                <ol class="list-decimal ml-5 space-y-2">
                                    <li><strong>Parent Field:</strong> Select which earlier question controls this field's visibility</li>
                                    <li><strong>Condition:</strong> Choose how to compare the answer</li>
                                    <li><strong>Value:</strong> Enter the expected answer value</li>
                                    <li><strong>Action:</strong> Choose what happens when the condition is met</li>
                                </ol>
                            </div>

                            <div>
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Available Conditions:</h4>
                                <ul class="space-y-2">
                                    <li><strong>Equals:</strong> Show when answer exactly matches the value</li>
                                    <li><strong>Not Equals:</strong> Show when answer doesn't match the value</li>
                                    <li><strong>Greater Than:</strong> Show when numeric answer is greater than value</li>
                                    <li><strong>Less Than:</strong> Show when numeric answer is less than value</li>
                                    <li><strong>Contains:</strong> Show when answer text contains the value</li>
                                    <li><strong>In Array:</strong> Show when answer is in a comma-separated list of values</li>
                                </ul>
                            </div>

                            <div>
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Available Actions:</h4>
                                <ul class="space-y-2">
                                    <li><strong>Show field (optional):</strong> Field appears but is not required</li>
                                    <li><strong>Show field (required):</strong> Field appears and must be filled</li>
                                    <li><strong>Hide field:</strong> Field is hidden when condition is met</li>
                                </ul>
                            </div>

                            <div class="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                <h4 class="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">Example:</h4>
                                <p class="mb-2">Question 1: "Was equipment damage found?" (Yes/No)</p>
                                <p class="mb-2">Question 2: "Describe the damage" (Long Text)</p>
                                <p class="font-semibold">Dependency Setup for Question 2:</p>
                                <ul class="ml-5 mt-2">
                                    <li>Parent Field: "Was equipment damage found?"</li>
                                    <li>Condition: Equals</li>
                                    <li>Value: Yes</li>
                                    <li>Action: Show field (required)</li>
                                </ul>
                                <p class="mt-2 text-xs">Result: Question 2 only appears when user answers "Yes" to Question 1</p>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button @click="showDependencyInfo = false" type="button" class="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Desired Answer Info Modal -->
        <div v-if="showDesiredAnswerInfo" class="fixed inset-0 z-[9999] overflow-y-auto" @click="showDesiredAnswerInfo = false">
            <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 z-[9998]"></div>
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full z-[9999]" @click.stop>
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="flex items-start justify-between mb-4">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Desired Answer Guide</h3>
                            <button @click="showDesiredAnswerInfo = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div class="space-y-4 text-sm text-gray-700 dark:text-gray-300 max-h-96 overflow-y-auto">
                            <div>
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">What is Desired Answer?</h4>
                                <p>Desired Answer defines what the "correct" or "expected" answer should be for a field. This is used for automatic validation, scoring, and flagging non-conformances in reports.</p>
                            </div>

                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Exact Value</h4>
                                <p class="mb-2">Field must match the exact specified value.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> For "Safety gear worn?" the desired answer is "Yes"</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> Enter the exact expected value (e.g., "Yes", "Pass", "Approved")</p>
                            </div>

                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Range (min-max)</h4>
                                <p class="mb-2">Numeric value must fall within a specified range.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> Temperature should be between 18°C and 22°C</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> Enter Min: 18, Max: 22</p>
                            </div>

                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Any Of (multiple correct)</h4>
                                <p class="mb-2">Answer must match ANY ONE of the acceptable values. Field passes if it matches any value in the list.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> Quality rating should be "Good" OR "Excellent"</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> Add multiple acceptable values: "Good", "Excellent"</p>
                            </div>

                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">None Of (multiple incorrect)</h4>
                                <p class="mb-2">Answer must NOT match any of the unacceptable values. Field fails if it matches any value in the list.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> Status should NOT be "Failed" or "Critical"</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> Add multiple unacceptable values: "Failed", "Critical"</p>
                            </div>

                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Any</h4>
                                <p class="mb-2">Any answer is acceptable. Field always passes as long as it's filled.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> "Inspector notes" - any comment is valid</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> No configuration needed</p>
                            </div>

                            <div class="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Always Pass</h4>
                                <p class="mb-2">Field is always considered correct, regardless of answer.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> Optional documentation fields that don't affect compliance</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> No configuration needed</p>
                            </div>

                            <div>
                                <h4 class="font-semibold text-gray-900 dark:text-white mb-2">Always Fail</h4>
                                <p class="mb-2">Field is always marked as non-conformant, regardless of answer. Used for tracking purposes.</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400"><strong>Example:</strong> Known issue fields that always flag for attention</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><strong>How to fill:</strong> No configuration needed</p>
                            </div>

                            <div class="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800 mt-4">
                                <h4 class="font-semibold text-green-900 dark:text-green-300 mb-2">💡 Tip:</h4>
                                <p>Desired answers are used for:</p>
                                <ul class="list-disc ml-5 mt-2 space-y-1">
                                    <li>Automatic pass/fail determination</li>
                                    <li>Scoring calculations</li>
                                    <li>Highlighting non-conformances in reports</li>
                                    <li>Triggering corrective actions</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button @click="showDesiredAnswerInfo = false" type="button" class="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, watch, ref } from 'vue';
import Toast from './Toast.vue';

const props = defineProps({
    field: Object,
    sections: Array,
});

const emit = defineEmits(['update:field', 'save', 'close']);

// Toast notification state
const toast = ref({
    show: false,
    type: 'error',
    message: ''
});

const showToast = (message, type = 'error') => {
    toast.value = {
        show: true,
        type,
        message
    };
};

const localField = ref(props.field ? { ...props.field } : null);
const fieldConfig = ref({});
const dependencyConfig = ref({
    parent_field_id: null,
    condition: 'equals',
    value: '',
    action: 'show_optional',
});
const desiredAnswerConfig = ref({
    type: null,
    value: '',
    min: null,
    max: null,
    values: [],
});

// Modal states
const showFieldTypeInfo = ref(false);
const showDependencyInfo = ref(false);
const showDesiredAnswerInfo = ref(false);

watch(() => props.field, (newField) => {
    if (newField) {
        localField.value = { ...newField };
        fieldConfig.value = newField.field_config ? (typeof newField.field_config === 'string' ? JSON.parse(newField.field_config) : newField.field_config) : {};

        // Initialize options if needed
        if (needsOptions.value && (!fieldConfig.value.options || fieldConfig.value.options.length === 0)) {
            fieldConfig.value.options = ['Option 1', 'Option 2'];
        }

        // Initialize rating if needed
        if (newField.field_type === 'rating_scale' && !fieldConfig.value.max) {
            fieldConfig.value.max = 5;
        }

        // Initialize dependency config
        if (newField.dependency_rule) {
            const rule = typeof newField.dependency_rule === 'string' ? JSON.parse(newField.dependency_rule) : newField.dependency_rule;
            dependencyConfig.value = {
                parent_field_id: rule.parent_field_id || null,
                condition: rule.condition || 'equals',
                value: rule.value || '',
                action: rule.action || 'show_optional',
            };
        } else {
            dependencyConfig.value = {
                parent_field_id: null,
                condition: 'equals',
                value: '',
                action: 'show_optional',
            };
        }

        // Initialize desired answer config
        if (newField.desired_answer) {
            const answer = typeof newField.desired_answer === 'string' ? JSON.parse(newField.desired_answer) : newField.desired_answer;

            let answerType = answer.type || null;
            const answerValue = answer.value || '';

            // Convert exact_value back to user-friendly types for yes_no and pass_fail_na fields
            if (answerType === 'exact_value' && newField.field_type === 'yes_no') {
                if (answerValue.toLowerCase() === 'yes') {
                    answerType = 'always_yes';
                } else if (answerValue.toLowerCase() === 'no') {
                    answerType = 'always_no';
                }
            } else if (answerType === 'exact_value' && newField.field_type === 'pass_fail_na') {
                if (answerValue.toLowerCase() === 'pass') {
                    answerType = 'always_pass';
                } else if (answerValue.toLowerCase() === 'fail') {
                    answerType = 'always_fail';
                }
            }

            desiredAnswerConfig.value = {
                type: answerType,
                value: answerValue,
                min: answer.min || null,
                max: answer.max || null,
                values: answer.any_of || answer.none_of || answer.values || [],
            };
        } else {
            desiredAnswerConfig.value = {
                type: null,
                value: '',
                min: null,
                max: null,
                values: [],
            };
        }
    } else {
        localField.value = null;
        fieldConfig.value = {};
        dependencyConfig.value = {
            parent_field_id: null,
            condition: 'equals',
            value: '',
            action: 'show_optional',
        };
        desiredAnswerConfig.value = {
            type: null,
            value: '',
            min: null,
            max: null,
            values: [],
        };
    }
}, { immediate: true });

// Watch for changes to dependency action and sync is_required
watch(() => dependencyConfig.value?.action, (newAction) => {
    if (!localField.value || !hasDependency.value) return;

    // Sync is_required with dependency action
    if (newAction === 'show_and_require') {
        localField.value.is_required = true;
    } else if (newAction === 'show_optional') {
        localField.value.is_required = false;
    }
});

// Watch for changes to desired answer type and auto-set values
watch(() => desiredAnswerConfig.value.type, (newType) => {
    if (!localField.value) return;

    const fieldType = localField.value.field_type;

    // Auto-set values for yes_no fields
    if (fieldType === 'yes_no') {
        if (newType === 'always_yes') {
            desiredAnswerConfig.value.value = 'yes';
        } else if (newType === 'always_no') {
            desiredAnswerConfig.value.value = 'no';
        } else {
            desiredAnswerConfig.value.value = '';
        }
    }

    // Auto-set values for pass_fail_na fields
    if (fieldType === 'pass_fail_na') {
        if (newType === 'always_pass') {
            desiredAnswerConfig.value.value = 'pass';
        } else if (newType === 'always_fail') {
            desiredAnswerConfig.value.value = 'fail';
        } else {
            desiredAnswerConfig.value.value = '';
        }
    }
});

const fieldTypeLabels = {
    yes_no: 'Yes/No',
    pass_fail_na: 'Pass/Fail/NA',
    multiple_choice: 'Multiple Choice',
    multiple_selection: 'Checkboxes',
    dropdown_select: 'Dropdown',
    text_input: 'Short Text',
    textarea: 'Long Text',
    number_input: 'Number',
    decimal_input: 'Decimal',
    rating_scale: 'Rating',
    date_picker: 'Date',
    datetime_picker: 'Date & Time',
    time_picker: 'Time',
    photo_capture: 'Photo',
    file_upload: 'File',
    signature: 'Signature',
};

const needsOptions = computed(() => {
    return localField.value && ['multiple_choice', 'multiple_selection', 'dropdown_select'].includes(localField.value.field_type);
});

const availableParentFields = computed(() => {
    if (!props.sections || !localField.value) return [];

    const allFields = [];
    props.sections.forEach(section => {
        if (section.fields) {
            section.fields.forEach(field => {
                // Exclude the current field
                if (field.id !== localField.value.id) {
                    allFields.push(field);
                }
            });
        }
    });

    return allFields;
});

// Available answer types based on field type
const availableAnswerTypes = computed(() => {
    if (!localField.value) return [];

    const fieldType = localField.value.field_type;

    // Yes/No field
    if (fieldType === 'yes_no') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'always_yes', label: 'Always Yes' },
            { value: 'always_no', label: 'Always No' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Pass/Fail/NA field
    if (fieldType === 'pass_fail_na') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'always_pass', label: 'Always Pass' },
            { value: 'always_fail', label: 'Always Fail' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Multiple Choice field
    if (fieldType === 'multiple_choice') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Checkboxes (Multiple Selection) field
    if (fieldType === 'multiple_selection') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
            { value: 'all_selected', label: 'All Selected' },
            { value: 'any_of', label: 'Any of (multiple correct)' },
            { value: 'none_of', label: 'None of (multiple incorrect)' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Dropdown field
    if (fieldType === 'dropdown_select') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Photo, File Upload, Signature - no desired answer
    if (fieldType === 'photo_capture' || fieldType === 'file_upload' || fieldType === 'signature') {
        return [
            { value: null, label: 'No desired answer' },
        ];
    }

    // Short Text field
    if (fieldType === 'text_input') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
        ];
    }

    // Long Text field - no desired answer
    if (fieldType === 'textarea') {
        return [
            { value: null, label: 'No desired answer' },
        ];
    }

    // Number field
    if (fieldType === 'number_input') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
            { value: 'range', label: 'Range (min-max)' },
            { value: 'greater_than', label: 'Greater than' },
            { value: 'greater_than_equal', label: 'Greater than or equal to' },
            { value: 'less_than', label: 'Less than' },
            { value: 'less_than_equal', label: 'Less than or equal to' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Decimal field
    if (fieldType === 'decimal_input') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
            { value: 'range', label: 'Range (min-max)' },
            { value: 'greater_than', label: 'Greater than' },
            { value: 'greater_than_equal', label: 'Greater than or equal to' },
            { value: 'less_than', label: 'Less than' },
            { value: 'less_than_equal', label: 'Less than or equal to' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Rating field
    if (fieldType === 'rating_scale') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'exact_value', label: 'Exact Value' },
            { value: 'range', label: 'Range (min-max)' },
            { value: 'greater_than', label: 'Greater than' },
            { value: 'greater_than_equal', label: 'Greater than or equal to' },
            { value: 'less_than', label: 'Less than' },
            { value: 'less_than_equal', label: 'Less than or equal to' },
            { value: 'any', label: 'Any answer is correct' },
        ];
    }

    // Date field
    if (fieldType === 'date_picker') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'before_submission_date', label: 'Before submission date' },
            { value: 'on_and_before_submission_date', label: 'On and before submission date' },
            { value: 'after_submission_date', label: 'After submission date' },
            { value: 'on_and_after_submission_date', label: 'On and after submission date' },
            { value: 'any', label: 'Any date' },
        ];
    }

    // Time field
    if (fieldType === 'time_picker') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'before_submission_time', label: 'Before submission time' },
            { value: 'on_and_before_submission_time', label: 'On and before submission time' },
            { value: 'after_submission_time', label: 'After submission time' },
            { value: 'on_and_after_submission_time', label: 'On and after submission time' },
            { value: 'any', label: 'Any time' },
        ];
    }

    // DateTime field
    if (fieldType === 'datetime_picker') {
        return [
            { value: null, label: 'No desired answer' },
            { value: 'before_submission_datetime', label: 'Before submission date & time' },
            { value: 'on_and_before_submission_datetime', label: 'On and before submission date & time' },
            { value: 'after_submission_datetime', label: 'After submission date & time' },
            { value: 'on_and_after_submission_datetime', label: 'On and after submission date & time' },
            { value: 'any', label: 'Any date & time' },
        ];
    }

    // Default options for other field types
    return [
        { value: null, label: 'No desired answer' },
        { value: 'exact_value', label: 'Exact Value' },
        { value: 'range', label: 'Range (min-max)' },
        { value: 'any_of', label: 'Any of (multiple correct)' },
        { value: 'none_of', label: 'None of (multiple incorrect)' },
        { value: 'any', label: 'Any answer is correct' },
        { value: 'always_pass', label: 'Always Pass' },
        { value: 'always_fail', label: 'Always Fail' },
    ];
});

// Check if the current field type should show value input
const shouldShowValueInput = computed(() => {
    if (!localField.value) return false;

    const fieldType = localField.value.field_type;
    const answerType = desiredAnswerConfig.value.type;

    // For yes_no and pass_fail_na, never show value input (auto-set)
    if (fieldType === 'yes_no' || fieldType === 'pass_fail_na') {
        return false;
    }

    // For photo, file upload, signature, long text - never show value input
    if (fieldType === 'photo_capture' || fieldType === 'file_upload' || fieldType === 'signature' || fieldType === 'textarea') {
        return false;
    }

    // For multiple_choice and dropdown_select, only show for exact_value
    if (fieldType === 'multiple_choice' || fieldType === 'dropdown_select') {
        return answerType === 'exact_value';
    }

    // For multiple_selection (checkboxes), only show for exact_value (not for all_selected)
    if (fieldType === 'multiple_selection') {
        return answerType === 'exact_value';
    }

    // For short text, only show for exact_value
    if (fieldType === 'text_input') {
        return answerType === 'exact_value';
    }

    // For numeric fields (number, decimal, rating), show for comparison operators and exact_value
    if (fieldType === 'number_input' || fieldType === 'decimal_input' || fieldType === 'rating_scale') {
        return ['exact_value', 'greater_than', 'greater_than_equal', 'less_than', 'less_than_equal'].includes(answerType);
    }

    // For other field types, show for exact_value
    return answerType === 'exact_value';
});

// Check if field has a dependency rule
const hasDependency = computed(() => {
    return dependencyConfig.value?.parent_field_id !== null && dependencyConfig.value?.parent_field_id !== undefined;
});

const getFieldTypeLabel = (type) => fieldTypeLabels[type] || type;

const addOption = () => {
    if (!fieldConfig.value.options) {
        fieldConfig.value.options = [];
    }
    fieldConfig.value.options.push('');
};

const removeOption = (index) => {
    fieldConfig.value.options.splice(index, 1);
};

const addDependency = () => {
    dependencyConfig.value = {
        parent_field_id: availableParentFields.value.length > 0 ? availableParentFields.value[0].id : null,
        condition: 'equals',
        value: '',
        action: 'show_optional',
    };
};

const removeDependency = () => {
    dependencyConfig.value = {
        parent_field_id: null,
        condition: 'equals',
        value: '',
        action: 'show_optional',
    };
};

const addDesiredAnswerValue = () => {
    if (!desiredAnswerConfig.value.values) {
        desiredAnswerConfig.value.values = [];
    }
    desiredAnswerConfig.value.values.push('');
};

const removeDesiredAnswerValue = (index) => {
    desiredAnswerConfig.value.values.splice(index, 1);
};

const saveField = () => {
    if (!localField.value.label) {
        showToast('Please enter a field label', 'warning');
        return;
    }

    if (needsOptions.value && (!fieldConfig.value.options || fieldConfig.value.options.length === 0)) {
        showToast('Please add at least one option for this field type', 'warning');
        return;
    }

    // Save config back to field
    localField.value.field_config = Object.keys(fieldConfig.value).length > 0 ? fieldConfig.value : null;

    // Save dependency rule
    if (dependencyConfig.value.parent_field_id) {
        localField.value.dependency_rule = {
            parent_field_id: dependencyConfig.value.parent_field_id,
            condition: dependencyConfig.value.condition,
            value: dependencyConfig.value.value,
            action: dependencyConfig.value.action,
        };
        // Also set parent_field_id directly on the field (for backend)
        localField.value.parent_field_id = dependencyConfig.value.parent_field_id;
        localField.value.is_primary = false;
    } else {
        localField.value.dependency_rule = null;
        localField.value.parent_field_id = null;
        localField.value.is_primary = true;
    }

    // Save desired answer
    if (desiredAnswerConfig.value.type) {
        const answerData = {};

        // Handle special answer types (always_yes, always_no, always_pass, always_fail)
        // Convert them to exact_value for storage
        if (desiredAnswerConfig.value.type === 'always_yes' || desiredAnswerConfig.value.type === 'always_no') {
            answerData.type = 'exact_value';
            answerData.value = desiredAnswerConfig.value.value; // 'yes' or 'no' (auto-set by watcher)
        } else if (desiredAnswerConfig.value.type === 'always_pass' || desiredAnswerConfig.value.type === 'always_fail') {
            answerData.type = 'exact_value';
            answerData.value = desiredAnswerConfig.value.value; // 'pass' or 'fail' (auto-set by watcher)
        } else {
            answerData.type = desiredAnswerConfig.value.type;

            if (desiredAnswerConfig.value.type === 'exact_value') {
                answerData.value = desiredAnswerConfig.value.value;
            } else if (desiredAnswerConfig.value.type === 'range') {
                answerData.min = desiredAnswerConfig.value.min;
                answerData.max = desiredAnswerConfig.value.max;
            } else if (desiredAnswerConfig.value.type === 'greater_than') {
                answerData.value = desiredAnswerConfig.value.value;
            } else if (desiredAnswerConfig.value.type === 'greater_than_equal') {
                answerData.value = desiredAnswerConfig.value.value;
            } else if (desiredAnswerConfig.value.type === 'less_than') {
                answerData.value = desiredAnswerConfig.value.value;
            } else if (desiredAnswerConfig.value.type === 'less_than_equal') {
                answerData.value = desiredAnswerConfig.value.value;
            } else if (desiredAnswerConfig.value.type === 'any_of') {
                answerData.any_of = desiredAnswerConfig.value.values;
            } else if (desiredAnswerConfig.value.type === 'none_of') {
                answerData.none_of = desiredAnswerConfig.value.values;
            } else if (desiredAnswerConfig.value.type === 'all_selected') {
                // all_selected - expects all checkbox options to be selected
                // Store the field config options for validation
                answerData.all_options = fieldConfig.value.options || [];
            }
        }

        localField.value.desired_answer = JSON.stringify(answerData);
    } else {
        localField.value.desired_answer = null;
    }

    emit('save', localField.value);
};
</script>
